/**
 * Holds the loaded preset list and the active-preset selection. Transitions
 * are tracked here as `{from, to, startMs}`; the actual lerp happens in
 * VisualizerEngine because it owns the GPU-bound param bundles.
 *
 * Presets are validated at registration time, so failures surface before any
 * frame ever runs.
 */

import type { VisualizerPreset } from './presetTypes';
import { validatePreset } from './validate';

export type TransitionState = {
  from: VisualizerPreset;
  to: VisualizerPreset;
  startMs: number;
  durationMs: number;
};

export type ActiveSelection = {
  current: VisualizerPreset;
  /** Non-null only while crossfading. Caller computes t = (now - startMs) / durationMs. */
  transition: TransitionState | null;
};

export class PresetRegistry {
  private list: VisualizerPreset[] = [];
  private currentIndex = 0;
  private transition: TransitionState | null = null;

  register(preset: VisualizerPreset): void {
    validatePreset(preset);
    if (this.list.some((p) => p.id === preset.id)) {
      throw new Error(`PresetRegistry: duplicate preset id "${preset.id}"`);
    }
    this.list.push(preset);
  }

  registerMany(presets: VisualizerPreset[]): void {
    for (const p of presets) this.register(p);
  }

  size(): number {
    return this.list.length;
  }

  ids(): string[] {
    return this.list.map((p) => p.id);
  }

  /** Returns the id at the given slot, or null if out of range. */
  idAt(index: number): string | null {
    return this.list[index]?.id ?? null;
  }

  currentId(): string | null {
    return this.list[this.currentIndex]?.id ?? null;
  }

  /** Returns the active selection (current + optional in-flight transition). */
  active(): ActiveSelection {
    const current = this.list[this.currentIndex];
    if (!current) throw new Error('PresetRegistry: no presets registered');
    return { current, transition: this.transition };
  }

  /**
   * Make a transition from the *current* preset to the named one. The
   * registry advances `currentIndex` immediately; the transition state stays
   * around for the lerp window.
   */
  loadById(id: string, nowMs: number): void {
    const idx = this.list.findIndex((p) => p.id === id);
    if (idx === -1) {
      throw new Error(
        `PresetRegistry: unknown preset "${id}". Known: ${this.ids().join(', ')}`,
      );
    }
    this.transitionTo(idx, nowMs);
  }

  loadAt(index: number, nowMs: number): void {
    if (index < 0 || index >= this.list.length) return;
    this.transitionTo(index, nowMs);
  }

  next(nowMs: number): void {
    this.transitionTo((this.currentIndex + 1) % this.list.length, nowMs);
  }

  prev(nowMs: number): void {
    const n = this.list.length;
    this.transitionTo((this.currentIndex - 1 + n) % n, nowMs);
  }

  random(nowMs: number): void {
    if (this.list.length <= 1) return;
    let idx = this.currentIndex;
    while (idx === this.currentIndex) {
      idx = Math.floor(Math.random() * this.list.length);
    }
    this.transitionTo(idx, nowMs);
  }

  /** Caller invokes this each frame after consuming the lerp output. */
  tickTransition(nowMs: number): void {
    if (this.transition && nowMs >= this.transition.startMs + this.transition.durationMs) {
      this.transition = null;
    }
  }

  private transitionTo(targetIndex: number, nowMs: number): void {
    if (targetIndex === this.currentIndex && !this.transition) return;
    const from = this.list[this.currentIndex];
    const to = this.list[targetIndex];
    if (!from || !to) return;
    this.transition = {
      from,
      to,
      startMs: nowMs,
      durationMs: to.transition.durationMs,
    };
    this.currentIndex = targetIndex;
  }
}
